import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import { StaticPageLayout } from '../../components/layout/StaticPageLayout';
import { PageMeta } from '../../components/seo/PageMeta';
import { PROXY_SEO_PAGES, type ProxySeoSlug } from './proxySeoPages';

const RELATED: ProxySeoSlug[] = [
  'buy-proxies',
  'residential-proxies',
  'mobile-proxies',
  'isp-proxies',
  'datacenter-proxies',
];

export function ProxySeoPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, '').replace(/\/$/, '') || 'buy-proxies';
  const { i18n } = useTranslation();
  const page = PROXY_SEO_PAGES[slug as ProxySeoSlug] ?? PROXY_SEO_PAGES['buy-proxies'];
  const copy = i18n.language?.startsWith('ar') ? page.ar : page.en;
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: copy.h1,
      description: copy.description,
      url: `https://rocketproxy.io${page.path}`,
      provider: {
        '@type': 'Organization',
        name: 'Rocket Proxy',
        url: 'https://rocketproxy.io/',
      },
      areaServed: 'Worldwide',
      mainEntity: {
        '@type': 'FAQPage',
        mainEntity: copy.faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    }),
    [copy, page.path],
  );

  return (
    <>
      <PageMeta title={copy.title} description={copy.description} path={page.path} jsonLd={jsonLd} />
      <StaticPageLayout title={copy.h1} subtitle={copy.subtitle}>
        <p className="text-sm leading-relaxed text-neutral-medium">{copy.intro}</p>
        <ul className="list-disc ps-5 space-y-2 text-sm leading-relaxed mt-4">
          {copy.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3 pt-6">
          <Link
            to="/register"
            className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-brand-accent text-white font-heading font-semibold text-sm"
          >
            {i18n.language?.startsWith('ar') ? 'إنشاء حساب' : 'Create account'}
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center justify-center h-11 px-5 rounded-full border border-neutral-dark font-heading font-semibold text-sm"
          >
            {i18n.language?.startsWith('ar') ? 'تسجيل الدخول' : 'Log in'}
          </Link>
        </div>
        <section className="pt-8 space-y-4" aria-labelledby="proxy-seo-faq">
          <h2 id="proxy-seo-faq" className="text-lg font-heading font-semibold">
            {i18n.language?.startsWith('ar') ? 'أسئلة شائعة' : 'Frequently asked questions'}
          </h2>
          {copy.faqs.map((item) => (
            <div key={item.q}>
              <h3 className="text-sm font-heading font-semibold">{item.q}</h3>
              <p className="text-sm leading-relaxed text-neutral-medium mt-1">{item.a}</p>
            </div>
          ))}
        </section>
        <nav className="pt-8" aria-label="Proxy types">
          <ul className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            {RELATED.filter((item) => item !== slug).map((item) => {
              const related = PROXY_SEO_PAGES[item];
              const label = i18n.language?.startsWith('ar') ? related.ar.h1 : related.en.h1;
              return (
                <li key={item}>
                  <Link to={related.path} className="text-brand-accent hover:underline">
                    {label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </StaticPageLayout>
    </>
  );
}
